export const API_ORIGIN = import.meta.env.VITE_API_ORIGIN ?? "https://api.aovtools.com";

export class ApiError extends Error {
	constructor(
		readonly status: number,
		message: string,
	) {
		super(message);
		this.name = "ApiError";
	}

	get isUnauthenticated(): boolean {
		return this.status === 401;
	}
}

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
	const headers = new Headers(init.headers);
	if (!headers.has("accept")) headers.set("accept", "application/json");
	const response = await fetch(`${API_ORIGIN}${path}`, {
		...init,
		credentials: "include",
		headers,
	});

	if (!response.ok) {
		const detail: unknown = await response.json().catch(() => null);
		const message =
			detail && typeof detail === "object" && "message" in detail
				? String(detail.message)
				: `Request failed with status ${response.status}`;
		throw new ApiError(response.status, message);
	}

	if (response.status === 204) return undefined as T;
	return (await response.json()) as T;
};

export const api = {
	get: <T>(path: string): Promise<T> => request<T>(path),
	post: <T>(path: string, body?: unknown): Promise<T> =>
		request<T>(path, {
			method: "POST",
			...(body === undefined
				? {}
				: { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
		}),
	remove: <T>(path: string): Promise<T> => request<T>(path, { method: "DELETE" }),
	put: <T>(path: string, body: unknown): Promise<T> =>
		request<T>(path, {
			method: "PUT",
			headers: { "content-type": "application/json" },
			body: JSON.stringify(body),
		}),
};

export const buildQuery = (params: Record<string, string | number | undefined>): string => {
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined && value !== "") search.set(key, String(value));
	}
	const query = search.toString();
	return query ? `?${query}` : "";
};
